import React, { useMemo } from 'react';
import ChatMessage from '../../../../../../components/ChatMessage';
import MessageAttachments from '../../../../../../components/ChatMessage/MessageAttachments';
import ScriptCard, { scriptsByMessage } from '../../../ScriptCard';
import ArtCard from '../../../ArtCard';
import { OfficialSource, SourceChips } from '../../../../../../components/SourceCitation';
import { revisionEvidence } from '../../../../../../data/sources';
import memoStudio, { studioKeys } from '../../../../../../utils/memoStudio';

const NONE = [];

// One message of the thread: the images sent with it, its text, the sources it cites and
// the script (or art) its run wrote. Memoized on what it shows, so the other messages stand
// still while one arrives, the run polls, the creator types or edits the script.
const ThreadMessage = memoStudio(function ThreadMessage({ studio: s, message, evidence, script, art, onOpenScript, onTeleprompter }) {
  const { t } = s;
  const images = message.attachments?.length ? <MessageAttachments attachments={message.attachments} t={t} /> : null;
  return <ChatMessage message={message} leading={images} trailing={evidence.length ? <SourceChips evidence={evidence} t={t} /> : null}>
    {evidence.length > 0 && <OfficialSource evidence={evidence} t={t} />}
    {script && <ScriptCard studio={s} revision={script} onOpen={onOpenScript} onTeleprompter={onTeleprompter} />}
    {art && <ArtCard studio={s} revision={art} onOpen={onOpenScript} />}
  </ChatMessage>;
}, ['t', ...studioKeys(ScriptCard, ArtCard)]);

// The project's messages. What a run generated from a sourced topic cites that topic's
// evidence; a run that wrote a script (or an art) attaches it to its result message.
function Thread({ studio: s, messages, onOpenScript, onTeleprompter }) {
  const { current, projectTopic } = s;
  const revisions = s.snapshot.revisions;
  const artRevisions = s.snapshot.artRevisions;
  const brief = current.brief?.topic?.kind === 'sourced' ? current.brief.topic : null;
  const cited = useMemo(() => {
    const map = {};
    if (!brief) return map;
    messages.forEach(m => {
      if (m.role !== 'assistant' || !m.run_id) return;
      const evidence = revisionEvidence(projectTopic, revisions.find(r => r.run_id === m.run_id), brief);
      if (evidence.length) map[m.id] = evidence;
    });
    return map;
  }, [messages, revisions, projectTopic, brief]);
  const scripts = useMemo(() => scriptsByMessage(messages, revisions), [messages, revisions]);
  const arts = useMemo(() => scriptsByMessage(messages, artRevisions || NONE), [messages, artRevisions]);
  return messages.map(m => <ThreadMessage key={m.id} studio={s} message={m} evidence={cited[m.id] || NONE} script={scripts[m.id] || null} art={arts[m.id] || null}
    onOpenScript={onOpenScript} onTeleprompter={onTeleprompter} />);
}

// Every `studio` field the thread and its messages read.
export default memoStudio(Thread, ['current', 'projectTopic', 'snapshot.revisions', 'snapshot.artRevisions', ...studioKeys(ThreadMessage)]);
